import React, { useEffect, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import { usePostLayoutState } from './PostLayoutProvider';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import { SIDEBAR_TRANSITION_DURATION } from '../app/layout/PageLayout';

const useOnMainEditorResize = (handler: () => void) => {
  const { isTOCExpanded } = usePostLayoutState();
  const didMountRef = useRef(false);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  const [isSidebarOpen] = useDebounce(
    useIsSidebarOpen(),
    SIDEBAR_TRANSITION_DURATION,
  );

  useEffect(() => {
    if (didMountRef.current) {
      handlerRef.current();
    } else {
      didMountRef.current = true;
    }
  }, [isSidebarOpen, isTOCExpanded]);

  useEffect(() => {
    const listener = () => handlerRef.current();
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
    };
  }, []);
};

interface MainEditorResizeHandlerProps {
  onMainEditorResize: () => void;
  children: React.ReactNode;
}

export const MainEditorResizeHandler = ({
  onMainEditorResize,
  children,
}: MainEditorResizeHandlerProps) => {
  useOnMainEditorResize(onMainEditorResize);
  return <>{children}</>;
};

export default useOnMainEditorResize;
